import type { ComponentPropsWithoutRef } from "react";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";
import { t } from "ttag";

import {
  TimeSlots,
  TimeSlotsEmptyState,
} from "@/app/components/legacy-components";
import { AppointmentDetailsCard } from "@/features/consultations/components";
import { useScheduleContext } from "@/features/consultations/contexts";
import { useTimeSlotsQuery } from "@/features/consultations/hooks";
import { PROVIDER_PREFERENCE } from "@/shared/models/legacy-constants";
import { Card } from "@/shared/ui";
import { formatBackendDate } from "@/shared/utils";

const cardVariants = tv({
  slots: {
    content: "flex flex-1 flex-col gap-4",
    timeSlotItem: "",
  },
  variants: {
    variant: {
      section: {
        timeSlotItem: "bg-white",
      },
    },
    disabled: {
      true: {
        content: "pointer-events-none",
      },
    },
  },
});

type CardVariants = VariantProps<typeof cardVariants>;

interface ScheduleAvailableTimeSlotCardProps
  extends ComponentPropsWithoutRef<typeof Card.Root>,
    CardVariants {
  titleStep: number;
}

export const ScheduleAvailableTimeSlotCard = ({
  children,
  variant,
  titleStep,
  ...props
}: ScheduleAvailableTimeSlotCardProps) => {
  const {
    language,
    scheduleDate: { appointmentDate, timeSlot },
    providerPreference,
    provider,
  } = useScheduleContext();

  const disabled =
    (providerPreference === PROVIDER_PREFERENCE.CUSTOM && !provider) ||
    !appointmentDate;

  const { isError, isLoading, isSuccess, data } = useTimeSlotsQuery({
    disabled,
  });

  const { content, timeSlotItem } = cardVariants({ variant, disabled });

  const verboseSelectedDate =
    appointmentDate && formatBackendDate(appointmentDate, "MMM dd, yyyy");
  const availability = verboseSelectedDate ? t` on ${verboseSelectedDate}` : "";

  return (
    <Card.Root {...props}>
      <Card.Title>{`${titleStep}. ${t`Available times${availability}`}`}</Card.Title>

      <Card.Content className={content()}>
        {disabled && <TimeSlots.Disabled />}

        {isError && <TimeSlots.Error />}

        {isLoading && <TimeSlots.Skeleton />}

        {isSuccess &&
          (data.length ? (
            <TimeSlots.Content>
              {data.map((timeSlot) => (
                <TimeSlots.Item
                  key={timeSlot.dateTime}
                  timeSlot={timeSlot}
                  className={timeSlotItem()}
                />
              ))}
            </TimeSlots.Content>
          ) : (
            <TimeSlotsEmptyState language={language} />
          ))}

        {timeSlot && (
          <AppointmentDetailsCard
            appointment={{ startTime: timeSlot.dateTime, provider }}
          />
        )}
      </Card.Content>

      {children}
    </Card.Root>
  );
};
